import React from 'react';
import { injectIntl } from 'react-intl';
import { parseClasses, autotranslateText, colorToArray, cssVar, isColorBright, isNotEmpty, i18nIntl, RGB_2_HEX } from '../../lib/ep';
import { useCSS } from '../../lib/hooks';

import ActionLink from '../builder/ActionLink';
import HTMLContainer from '../builder/HTMLContainer';
import ConfirmButton from '../builder/ConfirmButton';
import CopyLink from '../builder/CopyLink';
import Tooltip from '../builder/Tooltip';
import ActionButton from '../builder/ActionButton';

const DeleteRibbon = (props) => {
	if(props.onClick == undefined) return null;
	const Component = (props?.confirm ?? true) ? ConfirmButton : ActionButton;

	return (
		<Component
			icon='ep-delete'
			squared={true}
			_class={{ other:'delete_ribbon' }}
			_position={props?.position ?? 'top'}
			color="main"
			onClick={props.onClick}
		/>
	)
};

const RibbonWrapper = ({ type, ...props }) => {
	let classes = [props?.ribbon_type ?? 'single gui_small_icon'];

	const is_html = props?.is_html ?? false;
	if(is_html) classes.push('is_html');

	let classname = parseClasses(classes.join(" "), props._class, 'ribbons');

	let ribbon_styles = { ...props.style };

	const not_ribbon = props?.not_ribbon ?? false;

	if(!not_ribbon) {
		const color = props?.color ?? 'default';
		if(color != 'default' && color != 'custom') {
			if(color == 'gray') {
				// exception to increase readability:
				ribbon_styles['--ribbon-bg-color'] = 'var(--ep-gray-color)';
				ribbon_styles['--ribbon-text-color'] = 'var(--ep-black-color)';
			} else if(color == 'transparent') {
				ribbon_styles['--ribbon-bg-color'] = 'var(--main-bg-color)';
				ribbon_styles['--ribbon-bg-opacity'] = '0';
				ribbon_styles['--ribbon-text-color'] = 'var(--main-text-color)';
			} else {
				const is_color_var = (color.indexOf("--") != -1) ? true : false;

				const color_var = (is_color_var) ? `var(${color})` : `var(--ep-${color}-color)`;

				let color_to_array = colorToArray(props.color);
				const bg_color = (color_to_array !== false) ? color_to_array.join(", ") : color_var;
				
				ribbon_styles['--ribbon-bg-color'] = bg_color;
				
				if(props?.text_color != undefined) {
					ribbon_styles['--ribbon-text-color'] = props.text_color;	// "rrr, ggg, bbb" formatted
				} else {
					let is_bg_light = props?.is_light ?? null;
	
					if(is_bg_light == undefined) {
						let real_bg_color = (bg_color.indexOf("--") != -1) ? `rgba(${cssVar(bg_color)}, 1)` : `rgba(${bg_color}, 1)`;
						is_bg_light = isColorBright(RGB_2_HEX(real_bg_color), 150) ? true : false;
					}

					ribbon_styles['--ribbon-text-color'] = ((is_bg_light) ? 'var(--ep-black-color)' : 'var(--ep-white-color)');
				}
			}
		} else {
			classname += ` ${color}_colors_ribbon`;
		}
	} else {
		// ribbon without aspect of a ribbon:
		ribbon_styles['--ribbon-text-color'] = 'var(--main-text-color)';
		classname += ' not_ribbon';
	}
	
	return (
		<HTMLContainer
			type={props?.type ?? 'span'}
			id={props.id}
			className={classname}
			style={ribbon_styles}
		>
			{props.children}

			<DeleteRibbon
				onClick={props.onDelete}
				position={props.position}
				confirm={props.confirm}
			/>
		</HTMLContainer>
	)
}

const SingleLinkedRibbon = ({ title, ...props }) => {
	const icon_position = props?.icon_position ?? 'default';
	return (
		<RibbonWrapper {...props} ribbon_type='linked'>
			<ActionLink
				icon={props?.icon ?? null}
				_class={{other:`r${(icon_position != 'default') ? ` gui_align_icon_${icon_position}` : ''} gui_small_icon`}}
				tooltip={props.tooltip}
				onClick={props.onClick}
				type={props.link_type}
				_href={props._href}
				_target={props._target}
				autotranslate={props.autotranslate}
			>{title}</ActionLink>

			{(props.copy != undefined) && (
				<span>(<CopyLink
					copy_link={props.copy}
					tooltip={<Tooltip with_icon={false}>Click to copy</Tooltip>}
				>Copy</CopyLink>)</span>
			)}
		</RibbonWrapper>
	)
};

const SingleRibbon = (props) => {
	if(props.title == '') return null;

	const with_highlight = props?.with_highlight ?? false;

	let classes = ['r', 'gui_small_icon'];
	if(with_highlight) classes.push('with_highlight');
	if(props.icon != undefined) classes.push(props.icon);

	return (
		<RibbonWrapper
			{...props}
			is_html={(with_highlight) ? true : (props?.is_html ?? false)}
			ribbon_type='single'
		>
			<span class={classes.join(" ")}>
				{(with_highlight === false) && (
					autotranslateText(props.title, props.autotranslate, props.intl)
				)}

				{(with_highlight === true) && (
					<span class="d" dangerouslySetInnerHTML={{ __html:props.title }} />
				)}

				{(props.tooltip != undefined) && (
					props.tooltip
				)}
			</span>
		</RibbonWrapper>
	)
};

const CopyRibbon = (props) => {
	return (
		<RibbonWrapper {...props} ribbon_type='copy'>
			<CopyLink
				_class={{other:'r'}}
				copy_link={props.copy}
				tooltip={<Tooltip with_icon={false} position={props?.tooltip_position ?? 'top'}>Click to copy</Tooltip>}
			>{props.title}</CopyLink>
		</RibbonWrapper>
	)
};

const RibbonCollection = ({ items, ...props }) => {
	useCSS('comp/ribbons.css');
	
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const paint_if_empty = props?.paint_if_empty ?? false;

	if(paint_if_empty === false && (items == undefined || items.length == 0)) return null;

	const filtered_items = items.filter((r) => {
		if(r == undefined) return false;
		return r?.enabled ?? true;
	});

	if(paint_if_empty === false && filtered_items.length == 0) return null;

	if(props?.sort_ribbons ?? false) {
		filtered_items.sort((a, b) => {
			if(a.title < b.title) return -1;
			if(a.title > b.title) return 1;
			return 0;
		});
	}

	let classes = ['gui_ribbon_list'];
	const classname = parseClasses(classes.join(" "), props._class, 'ribbons');

	const parent_type = props?.type ?? 'div';
	const child_type = props?.subtype ?? 'span';
	
	return (
		<HTMLContainer
			type={parent_type}
			id={props.id}
			className={classname}
			style={props.style}
			paint_wrapper={props.paint_wrapper}
		>
			{(props.children != undefined) && (
				<span class="title">{props.children}</span>
			)}

			{filtered_items.length > 0 && filtered_items.map((item, i) => {
				const item_props = {
					...item,
					tooltip_position:item?.tooltip_position ?? 'top',
				};

				if(item.onClick != undefined || item._href != undefined) {
					return (
						<SingleLinkedRibbon
							key={`ribbon_${i}`}
							type={child_type}
							{...item_props}
						/>
					)
				}

				if(item.is_copy != undefined && item.is_copy) {
					return (
						<CopyRibbon
							key={`ribbon_${i}`}
							type={child_type}
							{...item_props}
						/>
					)
				}

				return (
					<SingleRibbon
						key={`ribbon_${i}`}
						type={child_type}
						intl={props.intl}
						{...item_props}
					/>
				)
			})}

			{(filtered_items.length == 0) && (
				<span class="title">{(isNotEmpty(props, 'empty_message')) ? props.empty_message : i18n("Empty")}</span>
			)}

			{(props.extra_contents != undefined) && (
				props.extra_contents
			)}
		</HTMLContainer>
	)
};

export default injectIntl(RibbonCollection);